import { Head, Link, router } from '@inertiajs/react'
import { Edit, Copy, ExternalLink, Database, FolderArchive, Clock, HardDrive, Settings } from 'lucide-react'
import Heading from '@/components/heading'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import AppLayout from '@/layouts/app-layout'
import { serversPath, serverPath, appPath, editAppPath, duplicateAppPath } from '@/routes'
import type { BreadcrumbItem } from '@/types'
import type { Server, App, BackupConfig, Backup } from '@/types'

type AppShowProps = {
  app: App & {
    server: Pick<Server, 'id' | 'name'>
    backup_configs: Pick<BackupConfig, 'id' | 'backup_type' | 'destination_type' | 'frequency' | 'enabled'>[]
    backups: Pick<Backup, 'id' | 'status' | 'frequency_type' | 'file_size' | 'completed_at'>[]
  }
}

export default function AppShow({ app }: AppShowProps) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: 'Servers', href: serversPath() },
    { title: app.server.name, href: serverPath(app.server.id) },
    { title: app.name, href: appPath(app.id) },
  ]

  function handleDuplicate() {
    const newName = prompt('Enter a name for the duplicated app:', `${app.name} (copy)`)
    if (newName) {
      router.post(duplicateAppPath(app.id), { new_name: newName })
    }
  }

  function formatBytes(bytes: number | null | undefined): string {
    if (!bytes) return 'N/A'
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    if (bytes === 0) return '0 Bytes'
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i]
  }

  function formatDate(date: string | null | undefined): string {
    if (!date) return 'Never'
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={app.name} />

      <div className="p-6">
        <div className="flex items-center justify-between">
        <Heading
          title={app.name}
          description={app.location || 'Application overview'}
        />
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleDuplicate}>
            <Copy className="mr-2 h-4 w-4" />
            Duplicate
          </Button>
          <Button variant="outline" asChild>
            <Link href={editAppPath(app.id)}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Link>
          </Button>
        </div>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>Application Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {app.app_url && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Primary URL</dt>
                <dd className="mt-1">
                  <a
                    href={app.app_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-primary hover:underline"
                  >
                    {app.app_url}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </dd>
              </div>
            )}

            {app.location && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Location</dt>
                <dd className="mt-1 font-mono text-sm">{app.location}</dd>
              </div>
            )}

            {app.db_type && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Database</dt>
                <dd className="mt-1 flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  {app.db_type} {app.db_name && `(${app.db_name})`}
                </dd>
              </div>
            )}

            {app.other_urls && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Other URLs</dt>
                <dd className="mt-1 space-y-1">
                  {app.other_urls.split(',').map((url, i) => (
                    <a
                      key={i}
                      href={url.trim()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-primary hover:underline"
                    >
                      {url.trim()}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ))}
                </dd>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Backup Configs */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Backup Configuration</CardTitle>
              <Button variant="outline" size="sm" asChild>
                <Link href={`/apps/${app.id}/backup_configs/new`}>
                  <Settings className="mr-2 h-4 w-4" />
                  Configure
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {app.backup_configs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No backup configurations</p>
            ) : (
              <div className="space-y-3">
                {app.backup_configs.map((config) => (
                  <div 
                    key={config.id} 
                    className="flex items-center justify-between rounded-lg border p-3"
                  >
                    <div className="flex items-center gap-3">
                      {config.backup_type === 'database' ? (
                        <Database className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <FolderArchive className="h-5 w-5 text-muted-foreground" />
                      )}
                      <div>
                        <p className="font-medium capitalize">{config.backup_type}</p>
                        <p className="text-xs text-muted-foreground">
                          {config.frequency} → {config.destination_type}
                        </p>
                      </div>
                    </div>
                    <Badge variant={config.enabled ? 'default' : 'secondary'}>
                      {config.enabled ? 'Active' : 'Disabled'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Backups */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Recent Backups</CardTitle>
            <CardDescription>Last 10 backup runs</CardDescription>
          </CardHeader>
          <CardContent>
            {app.backups.length === 0 ? (
              <p className="text-sm text-muted-foreground">No backups yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="pb-2 text-left font-medium">Type</th>
                      <th className="pb-2 text-left font-medium">Status</th>
                      <th className="pb-2 text-left font-medium">Size</th>
                      <th className="pb-2 text-left font-medium">Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {app.backups.map((backup) => (
                      <tr key={backup.id} className="border-b last:border-0">
                        <td className="py-2 capitalize">{backup.frequency_type}</td>
                        <td className="py-2">
                          <Badge 
                            variant={
                              backup.status === 'success' ? 'default' :
                              backup.status === 'error' ? 'destructive' :
                              backup.status === 'running' ? 'outline' : 'secondary'
                            }
                          >
                            {backup.status}
                          </Badge>
                        </td>
                        <td className="py-2">
                          <span className="flex items-center gap-1">
                            <HardDrive className="h-3 w-3" />
                            {formatBytes(backup.file_size)}
                          </span>
                        </td>
                        <td className="py-2">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(backup.completed_at)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notes */}
        {app.notes && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm dark:prose-invert max-w-none">
                {app.notes}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      </div>
    </AppLayout>
  )
}
